import { atom } from 'jotai';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import { KPIDashboardData, KPIHistoryData, KPIHistoryPoint } from './types';

export const kpiRefreshAtom = atom('');

const metricLabelOverrides: Record<string, string> = {
  ca_new_vulnerabilities: 'Active vulnerabilities',
  ca_fixed_vulnerabilities: 'Fixed vulnerabilities',
  wa_high_severity_findings: 'High-severity vulnerabilities',
  endpoint_high_severity_findings: 'High-severity vulnerabilities',
  sast_high_severity_findings: 'High-severity vulnerabilities',
};

export const kpiDashboardDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const _refresh = get(kpiRefreshAtom);

  if (!companyId || companyId <= 0) return undefined;

  try {
    // A KPI-specific cancellation scope keeps tenant switches and manual
    // refreshes from sharing request state with the other dashboard tabs.
    const [response, historyResponse] = await Promise.all([
      callApi(
        'dashboard/kpi/',
        'GET',
        { company_id: companyId },
        null,
        null,
        'dashboard/kpi/:kpi-dashboard',
      ),
      callApi(
        'dashboard/kpi/history/',
        'GET',
        { company_id: companyId, limit: 7 },
        null,
        null,
        'dashboard/kpi/history/:kpi-dashboard-history',
      ).catch(() => undefined),
    ]);
    const dashboardData = response?.data as KPIDashboardData | undefined;
    if (!dashboardData) return undefined;

    const historyData = historyResponse?.data as KPIHistoryData | undefined;
    const historyPoints = new Map<string, KPIHistoryPoint[]>();
    [...(historyData?.periods || [])].reverse().forEach((period) => {
      period.metrics.forEach((metric) => {
        const points = historyPoints.get(metric.metric_key) || [];
        points.push({
          period_end: period.period_end,
          value: metric.value,
        });
        historyPoints.set(metric.metric_key, points);
      });
    });

    return {
      ...dashboardData,
      sections: dashboardData.sections.map((section) => ({
        ...section,
        metrics: section.metrics.map((metric) => ({
          ...metric,
          // Normalize Cloud Agent wording once so tiles, details, charts,
          // tooltips, manual forms, and exports all display the same KPI name.
          label: metricLabelOverrides[metric.key] || metric.label,
          history_values: (historyPoints.get(metric.key) || []).map(
            (point) => point.value,
          ),
          history_points: historyPoints.get(metric.key) || [],
        })),
      })),
    };
  } catch (_error) {
    return undefined;
  }
});
